import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {useTranslation} from "react-i18next";
import {AuthService} from "../../service/AuthService";
import {
    MAINTENANCE_STATUS_PATH, activateMaintenance, isMaintenanceActive, isMaintenanceOn, isMaintenanceOver,
    registerMaintenanceStatusFetcher, subscribeMaintenance
} from "../../utils/maintenance";
import "./maintenance-page.css";

const POLL_INTERVAL_MS = 30000;
const POLL_TIMEOUT_MS = 10000;

// Plain axios on purpose: no auth header and no maintenance interceptor. The
// timestamp keeps any cache along the way out of it.
const fetchMaintenanceStatus = () =>
    axios.get(AuthService.coreApiURL + MAINTENANCE_STATUS_PATH,
        {timeout: POLL_TIMEOUT_MS, params: {_: Date.now()}});

// Lets the API interceptors ask this endpoint after a gateway error (see
// utils/maintenance.js); registered at import, before any screen calls the API.
registerMaintenanceStatusFetcher(fetchMaintenanceStatus);

// Full-screen notice drawn over everything (the login screen included) once
// any backend call has answered 503 {"code": "maintenance"}. Renders nothing
// otherwise. While shown it polls the backend's public maintenance/ endpoint
// and reloads the page when the window is over, so the app starts cleanly
// instead of resuming from screens whose requests failed.
const MaintenancePage = () => {

    const {t} = useTranslation();
    const [active, setActive] = useState(isMaintenanceActive());

    useEffect(() => {
        const unsubscribe = subscribeMaintenance(setActive);
        // Maintenance may have been flagged between the first render and this effect.
        setActive(isMaintenanceActive());
        // A signed-out visitor on the login screen makes no API call until they
        // submit; ask once on load so they see the notice straight away. The
        // backend answers this path before touching the database.
        fetchMaintenanceStatus()
            .then(res => {
                if (isMaintenanceOn(res.data))
                    activateMaintenance();
            }, () => {});
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (!active)
            return;

        // The page underneath stays mounted; stop it scrolling behind the notice.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        let stopped = false;
        let timer = null;
        const scheduleNext = () => {
            if (!stopped)
                timer = setTimeout(check, POLL_INTERVAL_MS);
        };
        const check = () => {
            fetchMaintenanceStatus()
                .then(res => {
                    if (stopped)
                        return;
                    if (isMaintenanceOver(res.data)) {
                        stopped = true;
                        window.location.reload();
                        return;
                    }
                    scheduleNext();
                }, scheduleNext);  // Still down or unreachable: try again silently.
        };
        scheduleNext();

        return () => {
            stopped = true;
            clearTimeout(timer);
            document.body.style.overflow = previousOverflow;
        };
    }, [active]);

    if (!active)
        return null;

    const isWhiteLabel = window.location.hostname.toLowerCase().indexOf('ecomscanner') !== -1;
    const appName = isWhiteLabel ? "eCOMSCANNER" : "PriceRest";

    return <div className="maintenance-page" role="dialog" aria-modal="true"
                aria-labelledby="maintenance-page-title" aria-describedby="maintenance-page-message">
        <div className="maintenance-page__card">
            {isWhiteLabel
                ? <img className="maintenance-page__logo maintenance-page__logo--white-label"
                       src="/assets/layout/images/logo_ecomscanner.png" alt={appName}/>
                : <img className="maintenance-page__logo"
                       src="/assets/layout/images/logo_gray.png" alt={appName}/>}
            <h1 id="maintenance-page-title" className="maintenance-page__title">
                {t('maintenance.title', "We'll be right back")}
            </h1>
            <p id="maintenance-page-message" className="maintenance-page__message">
                {t('maintenance.message', {
                    appName,
                    defaultValue: "{{appName}} is briefly unavailable for scheduled maintenance. " +
                        "This page will reload automatically when we're back."
                })}
            </p>
            <div className="maintenance-page__status" aria-hidden="true">
                <i className="pi pi-spin pi-spinner"/>
            </div>
        </div>
    </div>;
};

export default MaintenancePage;
